/**
 * Inner content component wrapped by error boundary
 * Contains the modal shell and step routing.
 */
import React, { useCallback } from "react";
import { useCompositionState } from "../../../../hooks";
import type { CompositionWizardProps } from "./types";
import { STEPS } from "./types";
import {
  SelectAssetsStep,
  WireInterfacesStep,
  ConfigureGatesStep,
  PreviewStep,
  CreateStep,
} from "./steps";

export const CompositionWizardContent: React.FC<CompositionWizardProps> = ({
  availableAssets,
  onClose,
  onComplete,
}) => {
  const composition = useCompositionState(availableAssets);

  const handleCreate = useCallback(() => {
    const composite = composition.createComposite();
    if (composite) {
      onComplete(composite);
      composition.reset();
    }
  }, [composition, onComplete]);

  const handleClose = useCallback(() => {
    composition.reset();
    onClose();
  }, [composition, onClose]);

  const currentStepIndex = STEPS.findIndex((s) => s.id === composition.step);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="composition-wizard-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        onKeyDown={(e) => e.key === "Escape" && handleClose()}
        role="button"
        tabIndex={0}
        aria-label="Close wizard"
      />

      {/* Modal */}
      <div className="relative bg-sa-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-sa-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-sa-text">Smart Asset Composer</h2>
              <p className="text-sm text-sa-text-muted">{STEPS[currentStepIndex]?.description}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-sa-text-dim hover:text-sa-text transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center mt-4 gap-2">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    index === currentStepIndex
                      ? "bg-sa-success/10 text-sa-success"
                      : index < currentStepIndex
                        ? "bg-sa-surface-hover text-sa-text-muted"
                        : "bg-sa-surface-hover text-sa-text-dim"
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                      index === currentStepIndex
                        ? "bg-green-600 text-white"
                        : index < currentStepIndex
                          ? "bg-sa-text-dim text-white"
                          : "bg-sa-border text-sa-text-muted"
                    }`}
                  >
                    {index < currentStepIndex ? (
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </span>
                  <span className="hidden sm:inline">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 ${
                      index < currentStepIndex ? "bg-green-400" : "bg-sa-surface-hover"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {composition.step === "select" && (
            <SelectAssetsStep
              availableAssets={availableAssets}
              selectedAssetIds={composition.selectedAssetIds}
              toggleAsset={composition.toggleAsset}
              isAssetSelected={composition.isAssetSelected}
            />
          )}

          {composition.step === "wire" && (
            <WireInterfacesStep
              availableAssets={availableAssets}
              selectedAssetIds={composition.selectedAssetIds}
              links={composition.links}
              addLink={composition.addLink}
              removeLink={composition.removeLink}
            />
          )}

          {composition.step === "gates" && (
            <ConfigureGatesStep
              links={composition.links}
              gateConfigs={composition.gateConfigs}
              setGateConfig={composition.setGateConfig}
            />
          )}

          {composition.step === "preview" && (
            <PreviewStep
              availableAssets={availableAssets}
              selectedAssetIds={composition.selectedAssetIds}
              links={composition.links}
            />
          )}

          {composition.step === "create" && (
            <CreateStep
              compositeName={composition.compositeName}
              setCompositeName={composition.setCompositeName}
              selectedAssetIds={composition.selectedAssetIds}
              links={composition.links}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-sa-border flex items-center justify-between">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-sa-text-muted hover:text-sa-text transition-colors"
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {currentStepIndex > 0 && (
              <button
                onClick={composition.prevStep}
                className="px-4 py-2 text-sm font-medium text-sa-text bg-sa-surface-hover hover:bg-sa-border rounded-lg transition-colors"
              >
                Back
              </button>
            )}
            {currentStepIndex < STEPS.length - 1 ? (
              <button
                onClick={composition.nextStep}
                disabled={!composition.canProceed}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-hover disabled:bg-sa-border disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={!composition.canProceed}
                className="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-hover disabled:bg-sa-border disabled:cursor-not-allowed rounded-lg transition-colors"
                data-testid="create-composite-button"
              >
                Create Composite
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
